import { type ReactNode } from 'react';

import { Box } from '@mui/material';

import { tokens } from '../../../theme/generated/tokens';
import { IconButton } from '../../buttons';
import * as Icons from '../../icons';
import { useDynamicGridContext } from '../useDynamicGridContext';

type CardCustomizeOverlayProps = {
  itemId: string;
  onHide: (id: string) => void;
  children: ReactNode;
};

export const CardCustomizeOverlay = ({
  itemId,
  onHide,
  children,
}: CardCustomizeOverlayProps) => {
  const { isCustomizing } = useDynamicGridContext();

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        ...(isCustomizing && {
          outline: '1px dashed',
          outlineColor: tokens.theme.border.selection.primary,
          borderRadius: tokens.theme.radius.sm,
        }),
      }}
    >
      {isCustomizing && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 8,
            transform: 'translateY(-50%)',
            zIndex: 2,
            bgcolor: tokens.theme.bg.structural.surface,
            borderRadius: tokens.theme.radius.sm,
            boxShadow: tokens.theme.shadows.elevation.medium,
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          <IconButton
            aria-label="Hide card"
            onClick={(e) => {
              e.stopPropagation();
              onHide(itemId);
            }}
            sx={{
              px: 8,
              py: 4,
              color: tokens.theme.fg.action.danger,
              '&:hover': {
                bgcolor: tokens.theme.bg.action.surfaceHover,
                color: tokens.theme.fg.action.danger,
              },
            }}
          >
            <Icons.Trash size={16} />
          </IconButton>
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          pointerEvents: isCustomizing ? 'none' : 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
